/**
 * Accordion class
 *
 * Handles each individual accordion item.
 */
class Accordion {
	/**
	 * Constructor.
	 *
	 * @param {HTMLElement} domNode the accordion dom node
	 */
	constructor( domNode ) {
		this.accordion = domNode;

		// Ensure the same accordion is not instantiated more than once.
		if ( this.accordion.dataset.initiated ) {
			return;
		}
		this.button = this.accordion.querySelector( 'button[aria-expanded]' );
		const controlsId = this.button.getAttribute( 'aria-controls' );
		const contentId = controlsId.replace( '-button', '-content' );
		this.content = document.getElementById( contentId );

		// Get initial open state.
		this.openState = this.button.getAttribute( 'aria-expanded' ) === 'true';
		this.imageId = this.accordion.dataset.accordionImageId;

		// add event listeners
		this.button.addEventListener( 'click', this.onButtonClick );

		//get the multi-accordion containers data-multi-accordion-id attribute
		//this will be used to check if the accordion is part of a multi-accordion
		this.containerId = this.accordion.closest( '.wp-block-qala-blocks-multi-image-accordions' )?.dataset.multiAccordionId;

		if ( this.containerId ) {
			// We are in a multi-accordion block.
			// Listen for the singleAccordionOpened event.
			wp.hooks.addAction( 'qalaBlocks.singleAccordionOpened', 'qala-blocks', this.handleOpenAccordion );
		}

		// Initialize the accordion to the correct state.
		if ( this.accordion.dataset.defaultOpen === 'true' ) {
			this.toggle( true );
		}

		// Set this as having been initiated.
		this.accordion.dataset.initiated = true;
	}

	/**
	 * Handle the singleAccordionOpened event.
	 * If another accordion is opened and this is already open, close it.
	 *
	 * @param {string} imageId The image ID to be shown.
	 * @param {string} containerId The container ID of the multi-accordion.
	 */
	handleOpenAccordion = ( imageId, containerId ) => {
		if ( containerId === this.containerId && imageId !== this.imageId && this.openState ) {
			this.toggle( ! this.openState );
		}
	}

	/**
	 * Run on clicking the button.
	 *
	 * @param {Event} event The event triggered with the click.
	 */
	onButtonClick = event => {
		event.preventDefault();
		// Toggle the accordion.
		this.toggle( ! this.openState );
		if ( this.containerId ) {
			// Only do this if we are in a multi-accordion block.
			// This will be listened to by the multi-image-accordions block.
			// It will hide all other images and show the one that is toggled.
			// Also we are doing this here because we want to trigger the event only when the accordion is clicked.
			// Having it in the toggle method will trigger the event even when the accordion is opened/closed programmatically or by another accordion,
			// which might cause unwanted behavior like closing the newly opened accordion content or loops.
			wp.hooks.doAction( 'qalaBlocks.accordionToggle', this.imageId, this.containerId, this.openState );
		}
	};

	/**
	 * Toggle open/closed state of the accordion.
	 *
	 * @param {boolean} openState Open state of the accordion.
	 */
	toggle = openState => {
		// don't do anything if the openState state doesn't change.
		if ( openState === this.openState ) {
			return;
		}

		// Toggle active class on the parent.
		this.accordion.classList.toggle( 'active' );
		// update the internal state
		this.openState = openState;

		// handle DOM updates
		this.button.setAttribute( 'aria-expanded', openState );
		if ( openState ) {
			this.toggleOpenState();
		} else {
			this.toggleClosedState();
		}
	}

	/**
	 * Initialize anchor link listeners.
	 *
	 * Qala Theme already listens to links with an anchor hash and uses a scrollTo animation
	 * which takes into account the header height. So keep it simple and just open the accordion here.
	 */
	initAnchorLinks() {
		const accordionId = this.accordion.id;

		if ( ! accordionId ) {
			return;
		}

		// Find all anchor links on the page that link to this accordion.
		const anchorLinks = document.querySelectorAll( `a[href$="#${ accordionId }"]` );

		anchorLinks.forEach( link => {
			// make sure the link is for this page.
			if ( link.pathname !== window.location.pathname ) {
				return;
			}
			link.addEventListener( 'click', () => {
				// it is definitely a link to this accordion on this page, so just open it.
				if ( ! this.openState ) {
					this.toggle( true );
				}
			} );
		} );
	}

	/**
	 * Check URL hash on page load and on hash changes
	 */
	checkUrlHash() {
		const accordionId = this.accordion.id;
		const doc = this.accordion.ownerDocument;

		if ( ! accordionId ) {
			return;
		}

		const handleHashMatch = () => {
			if ( window.location.hash === `#${ accordionId }` && ! this.openState ) {
				this.toggle( true );
			}
		};

		// Handle the page loads.
		if ( window.location.hash === `#${ accordionId }` ) {
			if ( doc.readyState === 'loading' ) {
				doc.addEventListener( 'DOMContentLoaded', handleHashMatch );
			} else {
				handleHashMatch();
			}
		}

		// Handle Back/Forward browser buttons
		doc.addEventListener( 'hashchange', handleHashMatch );
	}

	/**
	 * Toggle its open state.
	 */
	toggleOpenState = () => {
		this.content.removeAttribute( 'hidden' );
		this.content.classList.remove( 'wp-block-qala-blocks-single-accordion__content--hidden' );
	}

	/**
	 * Toggle its closed state.
	 */
	toggleClosedState = () => {
		this.content.setAttribute( 'hidden', true );
		this.content.classList.add( 'wp-block-qala-blocks-single-accordion__content--hidden' );
	}
}

// Initiate our class instance for each accordion.
const accordions = document.querySelectorAll( '.wp-block-qala-blocks-single-accordion' );
accordions.forEach( accordion => {
	const accordionInstance = new Accordion( accordion );

	accordionInstance.initAnchorLinks();
	accordionInstance.checkUrlHash();
} );
